import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Button, Alert, LinearProgress,
  Card, CardContent, Chip, Stack, Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// ─── Pricing Upload Page ──────────────────────────────────────────────────────
//
// Admin UI for uploading the weekly D-Tools catalog CSV export. The uploaded
// catalog is used to display current sell prices alongside Sage warehouse data
// (see /warehouse). Full-replace semantics — each upload truncates + reloads.
//
// Expected columns (from D-Tools "Products" export):
//   Manufacturer, Model, Category, Subcategory, Part Number,
//   Accounting Item Name, Short Description, Unit Cost, Unit Price,
//   Taxable, Use Tax, PID

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const PricingUpload = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const loadStatus = () => {
    setLoadingStatus(true);
    const token = localStorage.getItem('token');
    axios.get('/api/warehouse/pricing-import-status', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null))
      .finally(() => setLoadingStatus(false));
  };

  useEffect(() => { loadStatus(); }, []);

  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    setResult(null);
    setError(null);
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      setError('Please choose a .csv file.');
      setSelectedFile(null);
      return;
    }
    setSelectedFile(f);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setResult(null);
    setError(null);
    const token = localStorage.getItem('token');
    const form = new FormData();
    form.append('file', selectedFile);
    try {
      const r = await axios.post('/api/warehouse/pricing-import', form, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setResult(r.data);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadStatus();
    } catch (err) {
      const data = err.response?.data;
      if (data?.missing) {
        setError(`CSV is missing columns: ${data.missing.join(', ')}. Expected D-Tools "Products" export format.`);
      } else {
        setError(data?.error || data?.detail || err.message || 'Upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  const catalogStale = status?.latest_import && (() => {
    const days = (Date.now() - new Date(status.latest_import.imported_at).getTime()) / (1000 * 60 * 60 * 24);
    return days > 10;
  })();

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/warehouse')}
        sx={{ mb: 2 }}
      >
        Back to Warehouse
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px', mb: 1 }}>
        D-Tools Pricing Catalog
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a weekly D-Tools "Products" CSV export. The catalog powers the current sell price shown next to Sage warehouse items. Each upload replaces the entire catalog.
      </Typography>

      {/* Current catalog status */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500 }}>Current Catalog</Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadStatus}
              disabled={loadingStatus}
            >
              Refresh
            </Button>
          </Stack>
          {loadingStatus ? (
            <LinearProgress />
          ) : status?.latest_import ? (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip
                  icon={catalogStale ? <WarningIcon /> : <CheckCircleIcon />}
                  label={`Loaded ${fmtDate(status.latest_import.imported_at)}`}
                  color={catalogStale ? 'warning' : 'success'}
                  size="small"
                />
                {catalogStale && (
                  <Typography variant="caption" color="warning.main">
                    More than 10 days old — consider refreshing
                  </Typography>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                <strong>{Number(status.catalog?.total || 0).toLocaleString()}</strong> products
                {' · '}<strong>{Number(status.catalog?.priced || 0).toLocaleString()}</strong> with sell prices
                {' · '}uploaded by {status.latest_import.imported_by_name || 'unknown'}
                {' · '}source: {status.latest_import.filename}
              </Typography>
            </Box>
          ) : (
            <Alert severity="warning">No catalog loaded yet. Upload a D-Tools CSV to get started.</Alert>
          )}
        </CardContent>
      </Card>

      {/* Upload */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>Upload New Catalog</Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFilePick}
          style={{ display: 'none' }}
          id="pricing-csv-input"
        />
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <label htmlFor="pricing-csv-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<DescriptionIcon />}
              disabled={uploading}
            >
              Choose CSV File
            </Button>
          </label>
          {selectedFile && (
            <Typography variant="body2" color="text.secondary">
              {selectedFile.name} ({(selectedFile.size / 1024).toFixed(0)} KB)
            </Typography>
          )}
        </Stack>

        <Button
          variant="contained"
          color="primary"
          startIcon={<CloudUploadIcon />}
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          sx={{ mb: 2 }}
        >
          {uploading ? 'Uploading…' : 'Upload & Replace Catalog'}
        </Button>

        {uploading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {result?.ok && (
          <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
            {result.message}
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />
        <Typography variant="caption" color="text.secondary" component="div">
          <strong>Expected columns</strong> (from the D-Tools "Products" CSV export):
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5 }}>
          Manufacturer, Model, Category, Subcategory, Part Number, Accounting Item Name, Short Description, Unit Cost, Unit Price, Taxable, Use Tax, PID
        </Typography>
      </Paper>
    </Box>
  );
};

export default PricingUpload;
